import Joi from "joi";
import { NAME_AT_SIGN, NO_EMAIL_AS_NAME, nameLongEnough } from "../../helpers/publicName";

const eighteenYearsAgo = () => {
  const d = new Date();
  d.setFullYear(d.getFullYear() - 18);
  return d.toISOString().split("T")[0];
};

// B-24. Each message is shown under its field exactly as written, so each one
// says what to do. Joi's defaults ('"first" length must be at least 2
// characters long') were the first thing a new user read.
const nameRule = (empty) =>
  Joi.string()
    // 2+ characters, or one Chinese, Japanese or Korean letter (owner decision
    // Q-13). Refused as "string.min", so the message below is the one shown.
    .custom((value, helpers) => (nameLongEnough(value) ? value : helpers.error("string.min", { limit: 2 })))
    .max(256)
    // B-20. The name is public. Production sign-up hands it to the server
    // only after the account exists, where an email address can just be
    // dropped for "Collector Member", so it is caught here, while it can still
    // be fixed.
    .pattern(NAME_AT_SIGN, { invert: true })
    .required()
    .messages({
      "string.empty": empty,
      "any.required": empty,
      "string.min": "Use at least 2 characters",
      "string.max": "Use 256 characters or fewer",
      "string.pattern.invert.base": NO_EMAIL_AS_NAME,
    });

const signupSchema = {
  first: nameRule("Enter your first name"),
  last: nameRule("Enter your last name"),
  dateOfBirth: Joi.date()
    .max(eighteenYearsAgo())
    .required()
    .messages({
      "date.base": "Enter your date of birth",
      "date.max": "You must be at least 18 years old",
      "any.required": "Date of birth is required",
    }),
  // #107. TLD is {2,63}, not {2,5} — see the note in loginSchema.js. This rule
  // and that one must stay byte-identical; emailPattern.test.js asserts it.
  email: Joi.string()
    .ruleset.pattern(/^([a-zA-Z0-9_\-.]+)@([a-zA-Z0-9_\-.]+)\.([a-zA-Z]{2,63})$/)
    .rule({ message: "Enter a valid email address, like name@example.com" })
    .required()
    .messages({
      "string.empty": "Enter your email address",
      "any.required": "Enter your email address",
    }),
  // B-24. This pattern is not anchored, so `.{7,20}` only ever required 7
  // characters somewhere: 25 were accepted while the message promised "7-20".
  // The bound was never enforced, so it is no longer stated, and `.{7,}`
  // accepts exactly the same passwords. (Production signs up through
  // Supabase, which applies its own password policy on top of this one.)
  password: Joi.string()
    .ruleset.regex(
      /((?=.*\d{1})(?=.*[A-Z]{1})(?=.*[a-z]{1})(?=.*[!@#$%^&*-]{1}).{7,})/
    )
    .rule({
      message:
        "Use at least 7 characters, with an uppercase letter, a lowercase letter, a number and one of !@#$%^&*-",
    })
    .required()
    .messages({
      "string.empty": "Enter a password",
      "any.required": "Enter a password",
    }),
  // #65. The account cannot be created until the user affirmatively accepts the
  // Terms and Privacy Policy. Only `true` validates, so useForm blocks submit
  // while the box is unchecked.
  acceptedTerms: Joi.boolean().valid(true).required().messages({
    "any.only": "Please accept the Terms of Service and Privacy Policy to continue",
    "any.required": "Please accept the Terms of Service and Privacy Policy to continue",
  }),
};

export default signupSchema;
